import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qInventoryBatchAvailableQuantities,
  qInventoryBatchesByItem,
  qInventoryPickerCandidates,
  pickerCandidateRowToSelection,
  type InventoryBatchAvailabilityRow,
  type InventoryBatchRow,
  type InventoryPickerCandidateRow,
  type InventoryPickerSelection,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { NavIcon, useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import { InventoryPageShell } from './_shared';

// ─── Reasons & directions ───

const REASON_OPTIONS = [
  'Opening Balance',
  'Stock Correction',
  'Damaged',
  'Expired',
  'Lost',
  'Return',
  'Manual Add',
  'Manual Deduct',
  'Other',
];

type Direction = 'add' | 'deduct';

// ─── Local draft shapes ───

interface LineDraft {
  id: string;
  itemId: string | null;
  itemName: string;
  itemUnit: string | null;
  // PRD.264 — only batch-tracked items require a batch on each line.
  batchTracked: boolean;
  batchId: string | null;
  batchNumber: string | null;
  /** When the user creates a new batch inline, we hold its draft fields
   * here and persist them at save time alongside the adjustment lines. */
  newBatchDraft: NewBatchDraft | null;
  direction: Direction;
  quantity: string;
  reason: string;
  remarks: string;
}

interface NewBatchDraft {
  batchNumber: string;
  expiryDate: string;
  receivedDate: string;
  remarks: string;
}

function emptyLine(): LineDraft {
  return {
    id: crypto.randomUUID(),
    itemId: null,
    itemName: '',
    itemUnit: null,
    batchTracked: false,
    batchId: null,
    batchNumber: null,
    newBatchDraft: null,
    direction: 'add',
    quantity: '',
    reason: 'Stock Correction',
    remarks: '',
  };
}

// ─── Page ───

export function CreateInventoryAdjustmentPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const { db } = usePowerSyncDb();

  const today = new Date().toISOString().slice(0, 10);
  const [adjustmentNumber, setAdjustmentNumber] = useState('');
  const [adjustmentDate, setAdjustmentDate] = useState(today);
  const [location, setLocation] = useState('');
  const [remarks, setRemarks] = useState('');

  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [batchModalLineId, setBatchModalLineId] = useState<string | null>(null);

  function updateLine(id: string, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }
  function removeLine(id: string) {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== id)));
  }

  const handleCancel = () => navigate('/inventory/adjustments');

  const handleSave = async () => {
    setError(null);
    if (!db) return setError('Local database not ready. Please try again in a moment.');
    if (!orgId) return setError('Missing organization context. Please sign in again.');
    if (!adjustmentDate) return setError('Adjustment Date is required.');
    if (lines.length === 0) return setError('Add at least one adjustment line.');

    // Validate each line
    for (const line of lines) {
      if (!line.itemId) return setError('Select an item for every line.');
      const qty = Number(line.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        return setError('Enter a quantity greater than zero on every line.');
      }
      if (!line.reason) return setError('Select a reason for every line.');
      // Only batch-tracked items require a batch; non-batch items (e.g. a
      // Supply like Alcohol) adjust item-level stock with no batch.
      if (line.batchTracked && !line.batchId && !line.newBatchDraft) {
        return setError(
          line.direction === 'add'
            ? 'Select an existing batch or create a new batch for the line being added.'
            : 'Select a batch to deduct from for every deduct line.',
        );
      }
      if (
        line.batchTracked &&
        line.direction === 'add' &&
        line.newBatchDraft &&
        !line.newBatchDraft.batchNumber.trim()
      ) {
        return setError('New batch number is required.');
      }
    }

    // PRD.195 §7.1 — server-style guard: re-fetch live availability for
    // each deduct line at save time and reject if qty exceeds it. This is
    // a best-effort client check; concurrent deductions from another
    // device could still race past it. The append-only ledger keeps the
    // audit trail correct either way (a future PRD may add a server-side
    // constraint).
    for (const line of lines) {
      if (line.direction !== 'deduct') continue;
      // Batch-tracked lines check the selected batch; non-batch items check
      // the item's overall ledger balance.
      const scopeSql = line.batchId
        ? `WHERE inventory_batch_id = ?`
        : `WHERE inventory_item_id = ?`;
      const scopeParam = line.batchId ?? line.itemId;
      if (!scopeParam) continue;
      try {
        const rows = await db.getAll<{ available_quantity: number | null }>(
          `SELECT
              COALESCE(SUM(COALESCE(quantity_in, 0)), 0)
                - COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS available_quantity
            FROM stock_movements
            ${scopeSql}`,
          [scopeParam],
        );
        const avail = rows[0]?.available_quantity ?? 0;
        const qty = Number(line.quantity);
        if (qty > avail) {
          const where = line.batchId
            ? `batch ${line.batchNumber ?? line.batchId.slice(0, 8)}`
            : line.itemName || 'this item';
          return setError(`Cannot deduct ${qty} from ${where}: only ${avail} available.`);
        }
      } catch {
        // If the availability check itself fails, fall through and let
        // the actual save attempt surface the underlying error.
      }
    }

    setSaving(true);
    try {
      const adjustmentId = crypto.randomUUID();
      const now = new Date().toISOString();
      const number =
        adjustmentNumber.trim() || `ADJ-${now.slice(0, 10)}-${adjustmentId.slice(0, 4)}`;

      // 1) Header
      await db.execute(
        `INSERT INTO inventory_adjustments (
          id, org_id, adjustment_number, adjustment_date, location, remarks,
          status, created_by, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          adjustmentId,
          orgId,
          number,
          adjustmentDate,
          location.trim() || null,
          remarks.trim() || null,
          'posted',
          userId || null,
          now,
          now,
        ],
      );

      // 2) Lines + 3) Stock Movements
      for (const line of lines) {
        // Resolve / create batch first
        let batchId = line.batchId;
        if (!batchId && line.newBatchDraft) {
          batchId = crypto.randomUUID();
          await db.execute(
            `INSERT INTO inventory_batches (
              id, org_id, inventory_item_id, batch_number, expiry_date, received_date,
              status, remarks, created_at, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              batchId,
              orgId,
              line.itemId,
              line.newBatchDraft.batchNumber.trim(),
              line.newBatchDraft.expiryDate || null,
              line.newBatchDraft.receivedDate || adjustmentDate,
              'active',
              line.newBatchDraft.remarks.trim() || null,
              now,
              now,
            ],
          );
        }

        const lineId = line.id;
        const qty = Number(line.quantity);

        await db.execute(
          `INSERT INTO inventory_adjustment_lines (
            id, org_id, inventory_adjustment_id, inventory_item_id, inventory_batch_id,
            movement_direction, quantity, unit, reason, remarks,
            created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            lineId,
            orgId,
            adjustmentId,
            line.itemId,
            batchId,
            line.direction,
            qty,
            line.itemUnit,
            line.reason,
            line.remarks.trim() || null,
            now,
            now,
          ],
        );

        await db.execute(
          `INSERT INTO stock_movements (
            id, org_id, inventory_item_id, inventory_batch_id,
            source_type, source_id, source_line_id,
            movement_type, quantity_in, quantity_out, unit,
            movement_date, remarks, created_by, created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            line.itemId,
            batchId,
            'inventory_adjustment',
            adjustmentId,
            lineId,
            line.direction === 'add' ? 'adjustment_add' : 'adjustment_deduct',
            line.direction === 'add' ? qty : 0,
            line.direction === 'add' ? 0 : qty,
            line.itemUnit,
            adjustmentDate,
            line.remarks.trim() || null,
            userId || null,
            now,
            now,
          ],
        );
      }

      navigate('/inventory/adjustments');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed. Please try again.';
      setError(msg);
      setSaving(false);
    }
  };

  const editingLine = useMemo(
    () => lines.find((l) => l.id === batchModalLineId) ?? null,
    [lines, batchModalLineId],
  );

  return (
    <InventoryPageShell
      title="Create Adjustment"
      subtitle="Record a manual stock adjustment. Movements are written to the inventory ledger."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <>
          <Button variant="outline" size="md" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="gradient" size="md" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Post Adjustment'}
          </Button>
        </>
      }
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          background: 'var(--bg-app, #f8fafc)',
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {error && (
            <div
              role="alert"
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--sp-3) var(--sp-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}

          {/* Header card */}
          <Card title="Adjustment Details">
            <Grid2>
              <Field label="Adjustment No.">
                <TextInput
                  value={adjustmentNumber}
                  onChange={setAdjustmentNumber}
                  placeholder="Auto-generated if blank"
                />
              </Field>
              <Field label="Date" required>
                <TextInput type="date" value={adjustmentDate} onChange={setAdjustmentDate} />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Location">
                <TextInput value={location} onChange={setLocation} placeholder="Main Pharmacy" />
              </Field>
              <Field label="Remarks">
                <TextInput
                  value={remarks}
                  onChange={setRemarks}
                  placeholder="Optional adjustment-level notes"
                />
              </Field>
            </Grid2>
          </Card>

          {/* Lines */}
          <Card
            title="Lines"
            actions={
              <Button
                variant="outline"
                size="md"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                + Add Line
              </Button>
            }
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                <thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>Batch</Th>
                    <Th>Direction</Th>
                    <Th align="right">Quantity</Th>
                    <Th>Unit</Th>
                    <Th>Reason</Th>
                    <Th>Remarks</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <LineRow
                      key={line.id}
                      line={line}
                      onChange={(patch) => updateLine(line.id, patch)}
                      onPickBatch={() => setBatchModalLineId(line.id)}
                      onRemove={() => removeLine(line.id)}
                      canRemove={lines.length > 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {editingLine && (
        <BatchSelectorModal
          line={editingLine}
          onClose={() => setBatchModalLineId(null)}
          onApply={(patch) => {
            updateLine(editingLine.id, patch);
            setBatchModalLineId(null);
          }}
        />
      )}
    </InventoryPageShell>
  );
}

// ─── Line row ───

function LineRow({
  line,
  onChange,
  onPickBatch,
  onRemove,
  canRemove,
}: {
  line: LineDraft;
  onChange: (patch: Partial<LineDraft>) => void;
  onPickBatch: () => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const tdStyle: CSSProperties = {
    padding: '6px 8px',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'top',
  };
  return (
    <tr>
      <td style={{ ...tdStyle, minWidth: 240 }}>
        <ItemPicker
          value={line.itemName}
          onSelect={(selection) => {
            onChange({
              itemId: selection.inventoryItemId,
              itemName: selection.itemName,
              itemUnit: selection.inventoryUnit,
              batchTracked: selection.batchTracked,
              // Selecting a different item invalidates any prior batch.
              batchId: null,
              batchNumber: null,
              newBatchDraft: null,
            });
          }}
        />
      </td>
      <td style={{ ...tdStyle, minWidth: 180 }}>
        {line.itemId && !line.batchTracked ? (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            Not batch-tracked
          </span>
        ) : line.itemId ? (
          <button
            type="button"
            onClick={onPickBatch}
            style={{
              height: 32,
              padding: '0 8px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background:
                line.batchNumber || line.newBatchDraft
                  ? 'var(--color-success-bg)'
                  : 'var(--bg-surface)',
              color:
                line.batchNumber || line.newBatchDraft
                  ? 'var(--color-success-text)'
                  : 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {line.batchNumber
              ? `Batch ${line.batchNumber}`
              : line.newBatchDraft
                ? `New: ${line.newBatchDraft.batchNumber || '(unset)'}`
                : 'Select batch…'}
          </button>
        ) : (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            Pick item first
          </span>
        )}
      </td>
      <td style={tdStyle}>
        <select
          value={line.direction}
          onChange={(e) => onChange({ direction: e.target.value as Direction })}
          style={selectStyle}
        >
          <option value="add">Add</option>
          <option value="deduct">Deduct</option>
        </select>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          placeholder="0"
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td style={tdStyle}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {line.itemUnit ?? '—'}
        </span>
      </td>
      <td style={tdStyle}>
        <select
          value={line.reason}
          onChange={(e) => onChange({ reason: e.target.value })}
          style={selectStyle}
        >
          {REASON_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={line.remarks}
          onChange={(e) => onChange({ remarks: e.target.value })}
          placeholder="Optional"
          style={inputStyle}
        />
      </td>
      <td style={tdStyle}>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Remove line"
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-muted)',
            cursor: canRemove ? 'pointer' : 'not-allowed',
            opacity: canRemove ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <NavIcon name="trash" />
        </button>
      </td>
    </tr>
  );
}

// ─── Item Picker (reuses PRD.188 candidate query) ───

function ItemPicker({
  value,
  onSelect,
}: {
  value: string;
  onSelect: (selection: InventoryPickerSelection) => void;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLInputElement | null>(null);
  // The picker lives inside an `overflowX: auto` table wrapper which
  // (per CSS spec) implicitly clips overflow-y too. We portal the
  // dropdown to document.body and position it from the input's
  // bounding rect so it can never be clipped or hidden behind any
  // floating UI in the page chrome.
  const [anchorRect, setAnchorRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => setQuery(value), [value]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      // The portaled dropdown is outside containerRef, so check it
      // separately via a data attribute marker.
      let el: Node | null = target;
      while (el && el !== document.body) {
        if (el instanceof HTMLElement && el.dataset?.adjustmentPicker === '1') return;
        el = el.parentNode;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setAnchorRect(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // PRD.205 — adjustments accept Medicine and Product items.
  // PRD.264 — Supplies too (incl. non-sellable expendables): adjustments are
  // inventory management, so sellability must not gate them.
  const { data: candidateRows = [] } = usePowerSyncQuery<InventoryPickerCandidateRow>(
    qInventoryPickerCandidates(debounced, ['medicine', 'product', 'supply']),
  );

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={anchorRef}
        type="text"
        placeholder="Search inventory items (Medicine or Product)…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />
      {open &&
        candidateRows.length > 0 &&
        anchorRect &&
        createPortal(
          <div
            role="listbox"
            data-adjustment-picker="1"
            style={{
              position: 'fixed',
              top: anchorRect.top + 4,
              left: anchorRect.left,
              width: anchorRect.width,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              maxHeight: 280,
              overflowY: 'auto',
              zIndex: 1000,
            }}
          >
            {candidateRows.slice(0, 20).map((row) => {
              const selection = pickerCandidateRowToSelection(row);
              // PRD.264 — badge from the raw item_type so supplies read as
              // "Supply" (the picker selection collapses non-product to medicine).
              const rawType = (row.item_type ?? 'medicine').toLowerCase();
              const typeBadge =
                rawType === 'product'
                  ? { label: 'Product', bg: '#eef2ff', color: '#4338ca' }
                  : rawType === 'supply'
                    ? { label: 'Supply', bg: '#ecfeff', color: '#0e7490' }
                    : {
                        label: 'Medicine',
                        bg: 'var(--color-info-bg, #dbeafe)',
                        color: 'var(--color-info-text, #1e40af)',
                      };
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => {
                    onSelect(selection);
                    setQuery(selection.itemName);
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--bg-hover, rgba(0,0,0,0.04))';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  {/* PRD.205 / PRD.264 — type badge so Medicine / Product /
                    Supply is unambiguous in the adjustment picker. Selection
                    still flows through the same onSelect handler — no per-type
                    write differences. */}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0 6px',
                      height: 18,
                      borderRadius: 'var(--radius-full)',
                      background: typeBadge.bg,
                      color: typeBadge.color,
                      fontSize: 9,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {typeBadge.label}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semi)',
                        color: 'var(--text)',
                      }}
                    >
                      {selection.itemName}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginTop: 1,
                      }}
                    >
                      {[
                        selection.sku,
                        selection.inventoryUnit && `Unit: ${selection.inventoryUnit}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── Batch Selector Modal ───

function BatchSelectorModal({
  line,
  onClose,
  onApply,
}: {
  line: LineDraft;
  onClose: () => void;
  onApply: (patch: Partial<LineDraft>) => void;
}) {
  const brandTokens = useBrandTokens();
  const itemId = line.itemId!;

  const { data: batchRows = [] } = usePowerSyncQuery<InventoryBatchRow>(
    qInventoryBatchesByItem(itemId),
  );
  const { data: availabilityRows = [] } = usePowerSyncQuery<InventoryBatchAvailabilityRow>(
    qInventoryBatchAvailableQuantities(itemId),
  );
  const availabilityMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of availabilityRows) map.set(a.inventory_batch_id, a.available_quantity ?? 0);
    return map;
  }, [availabilityRows]);

  const [creating, setCreating] = useState<NewBatchDraft | null>(null);

  const allowDeductAvailability = (batchId: string) => availabilityMap.get(batchId) ?? 0;

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Select Batch"
      maxWidth={640}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
          {line.direction === 'add' && !creating && (
            <Button
              variant="outline"
              size="md"
              onClick={() =>
                setCreating({
                  batchNumber: '',
                  expiryDate: '',
                  receivedDate: '',
                  remarks: '',
                })
              }
            >
              + New Batch
            </Button>
          )}
          {creating ? (
            <div style={{ display: 'flex', gap: 'var(--sp-3)', marginLeft: 'auto' }}>
              <Button variant="outline" size="md" onClick={() => setCreating(null)}>
                Cancel
              </Button>
              <Button
                variant="gradient"
                size="md"
                onClick={() => {
                  if (!creating.batchNumber.trim()) return;
                  onApply({
                    batchId: null,
                    batchNumber: creating.batchNumber.trim(),
                    newBatchDraft: { ...creating, batchNumber: creating.batchNumber.trim() },
                  });
                }}
              >
                Use New Batch
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="md" onClick={onClose} style={{ marginLeft: 'auto' }}>
              Close
            </Button>
          )}
        </div>
      }
    >
      {creating ? (
        <div
          style={{
            padding: 'var(--sp-3) var(--sp-5) var(--sp-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <Field label="Batch Number" required>
            <TextInput
              value={creating.batchNumber}
              onChange={(v) => setCreating({ ...creating, batchNumber: v })}
              placeholder="e.g., LOT-2026-001"
            />
          </Field>
          <Grid2>
            <Field label="Expiry Date">
              <TextInput
                type="date"
                value={creating.expiryDate}
                onChange={(v) => setCreating({ ...creating, expiryDate: v })}
              />
            </Field>
            <Field label="Received Date">
              <TextInput
                type="date"
                value={creating.receivedDate}
                onChange={(v) => setCreating({ ...creating, receivedDate: v })}
              />
            </Field>
          </Grid2>
          <Field label="Remarks">
            <TextInput
              value={creating.remarks}
              onChange={(v) => setCreating({ ...creating, remarks: v })}
              placeholder="Optional"
            />
          </Field>
        </div>
      ) : batchRows.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-8)',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No batches available for this item yet.
          <br />
          {line.direction === 'add'
            ? 'Add stock by creating a new batch first.'
            : 'You must add stock with a batch before deducting.'}
        </div>
      ) : (
        <div style={{ padding: 'var(--sp-3) var(--sp-5) var(--sp-5)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Batch Number</Th>
                <Th>Expiry</Th>
                <Th align="right">Available</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {batchRows.map((b) => {
                const avail = allowDeductAvailability(b.id);
                const disabled = line.direction === 'deduct' && avail <= 0;
                return (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 6px', fontSize: 'var(--font-size-sm)' }}>
                      {b.batch_number ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {b.expiry_date ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        textAlign: 'right',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: avail > 0 ? 'var(--text)' : 'var(--text-muted)',
                      }}
                    >
                      {avail}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <Button
                        variant="outline"
                        size="md"
                        disabled={disabled}
                        onClick={() =>
                          onApply({
                            batchId: b.id,
                            batchNumber: b.batch_number,
                            newBatchDraft: null,
                          })
                        }
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ─── Small primitives ───

function Card({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: CSSProperties = {
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-family)',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, height: 40 }}
    />
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}
